import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom';
import GTable, { GTableActionColumn, ITableRowAction } from '../../../../components/elements/GTable/GTable';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useModal from '../../../../hooks/modalHook';
import usePolicy from '../../../../hooks/policyHook';
import useRoute from '../../../../hooks/routesHook';
import { formatCurrencyAmount, formatNumberValue, getFormattedDate, getPhoneNumberFromShortCodeFormat, onRemoveFilter, showToast } from '../../../../utils/miscFunctions';
import useUserAccount from '../../../../hooks/userAccountHook';
import { getSelectedCountry } from '../../../../states/slices/resourceSlice';
import { useDispatch, useSelector } from 'react-redux';
import useDownload from '../../../../hooks/downloadHook';
import Page from '../../../../components/PageWrapper';
import { AlertTriangle, CheckCircle2, Eye, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import ToolTip from '../../../../components/ToolTip';
import TableFilter from '../../../../components/elements/GTable/InGTableComponents/TableFilter';
import TableFilterForm from '../../../../components/elements/GTable/InGTableComponents/TableFilter/TableFilterForm';
import { ITableFilterItem } from '../../../../resources/interfaces';
import { getAllInsurers } from '../../../../services/insurer';
import { ArrowPathIcon } from '@heroicons/react/24/solid';
import { useTableFilters } from '../../../../hooks/useTableFilters';
import useBroker from '../../../../hooks/brokerHook';
import RouteLink from '../../../../components/elements/GTable/InGTableComponents/RouteLink';
import { CLEAR_QUOTE_WIDGET_PAYLOAD } from '../../../../states/slices/quoteWidgetSlice';


const Renewals = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { user } = useUserAccount();
    const currentPage = Number(searchParams.get('page')) || 0;
    const [selectedYearMonth, setSelectedYearMonth] = useState<Date>(new Date());
    const month = selectedYearMonth.getMonth() + 1;
    const year = selectedYearMonth.getFullYear();
    const { PolicyModalComponent, PolicyTypesBadge, getAllExpiringPolicies, renewMotorPolicy, markAsRenewed } = usePolicy();
    const { exportFilteredRenewals } = useBroker()
    const { getDashboardBase } = useRoute();
    const { DownloadHookModal } = useDownload();
    const dispatch = useDispatch();
    const { ModalComponent, updateModalStates } = useModal();
    const [showPerPage, setShowPerPage] = useState<number>(25);
    const [networkError, setNetworkError] = useState<boolean>(false)
    const selectedCountry = useSelector(getSelectedCountry);
    const countryId = selectedCountry?.id ?? 1
    const pageRef = useRef<HTMLDivElement>(null)
    const [insurers, setInsurers] = useState<any>([]);
    const [openFilter, setOpenFilter] = useState<boolean>(false);
    const [renewalState, setRenewalState] = useState<string>('PENDING');
    const [searchString, setSearchString] = useState<string>('')
    const queryClient = useQueryClient();

    const invalidateRenewals = () => {
        queryClient.invalidateQueries({ queryKey: ['getPolicyRenewals'] });
    }

    const getSelectedMetric = (): string => {
        switch (renewalState) {
            case 'LOST':
                return 'lost policy'
            case 'DUE':
                return 'due for renewal policy'
            case 'RENEWED':
                return 'renewed policy'

            default:
                return 'upcoming renewal'
        }
    }

    const {
        filters: tableFilters,
        setFilters,
        getFilterValue,
    } = useTableFilters({
        initialFilters: [],
        syncWithUrl: true,
    });

    const getAllBrollyInsurers = () => {
        getAllInsurers(countryId)
            .then((response: any) => {
                const allInsurers = response?.data?.map((insurer: any) => ({
                    name: `${insurer.name}`,
                    value: Number(insurer.id)
                }));
                setInsurers(allInsurers);
            })
            .catch((error: Error) => { })
    };

    useEffect(() => {
        setFilters(getDefaultFilters());
    }, [insurers]);

    useQuery({ queryKey: ['getAllBrollyInsurers', countryId], queryFn: getAllBrollyInsurers })

    function getDefaultFilters() {
        const filters: ITableFilterItem[] = [
            {
                name: 'insurer',
                value: '',
                value2: '',
                type: 'select',
                list: insurers,
                placeholder: 'Select insurer',
                label: 'Insurer',
                showSearch: true
            },
            {
                name: 'expiry_date',
                value: '',
                type: 'dateRange',
                placeholder: 'Select expiry date',
                label: 'Expiry date'
            },
        ]

        return filters;

    }

    const today = new Date()
    const dayNinety = new Date()
    dayNinety.setDate(today.getDate() + 90)

    const insurerId = getFilterValue('insurer');
    const expiryDateFilter = getFilterValue('expiry_date') || {};
    const expiredTo = expiryDateFilter?.to || getFormattedDate({ date: dayNinety, toServer: true });
    const expiredFrom = expiryDateFilter?.from || getFormattedDate({ date: today, toServer: true });
    const search = searchString?.toLocaleLowerCase()
    const brokerId = user?.isInternalAdmin() ? undefined : Number(user?.company?.id)

    const { mutate: exportRenewalsFromFilters } = useMutation({
        mutationFn: (): any => {
            return exportFilteredRenewals(brokerId, search, expiredFrom, expiredTo, insurerId);
        },
    })

    const getPolicyRenewals = async () => {

        try {
            const response = await getAllExpiringPolicies(currentPage, showPerPage, renewalState, insurerId, expiredTo, expiredFrom, undefined, undefined, search)
            if (response) {

                return response;
            }
        } catch (error: any) {
            showToast.error(error.description)
            setNetworkError(true)
        } finally {
            //setIsLoading(false)
        }
    }

    const getPolicyDetailsRoute = (policy: IPolicyRenewal) => {

        if (policy.policyType === 'VEHICLE') {
            return `${getDashboardBase()}/policies/${policy.policyNumber}?type=motor`
        }

        return `${getDashboardBase()}/policies/${policy.policyNumber}?type=non-motor`
    }

    const getTableRowActions = (data: IPolicyRenewal): ITableRowAction[] => {
        const actions = [
            {
                label: 'View',
                icon: <Eye className='h-4 w-4' />,
                callback: (policy: any) => {
                    navigate(getPolicyDetailsRoute(policy), { replace: true });
                },
                data: data
            },
        ];

        if (data?.priority && data.priority.toLowerCase() !== 'resolved' && !data.renewed) {
            actions.push(
                {
                    label: 'Mark as renewed',
                    icon: <CheckCircle2 className="h-4 w-4" />,
                    callback: () => markAsRenewed({ customerName: data?.customerName, policyNumber: data?.policyNumber, policyType: data?.policyType, onProceed: invalidateRenewals }),
                    data: data
                }
            )
        }

        if (data.policyType === 'VEHICLE' && !user.isInternalAdmin()) {
            actions.push({
                label: 'Renew policy',
                icon: <ArrowPathIcon className="h-4 w-4" />,
                callback: () => {

                    dispatch(CLEAR_QUOTE_WIDGET_PAYLOAD())
                    renewMotorPolicy(data);
                },
                data: data
            })
        }

        return actions;
    };

    const priorityName = (priority: string) => {
        if (priority === 'resolved') {
            return 'Renewed';
        } else
            if (priority === 'urgent') {
                return 'Urgent';
            } else if (priority === 'high') {
                return 'At Risk';
            }
            else {
                return null;
            }
    }

    const columns = useMemo(
        () => [
            {
                Header: 'M',
                columns: [
                    ...(user?.isInternalAdmin() ? [{
                        Header: 'Policy ID',
                        accessor: 'policyNumber',
                        Cell: (row: any) => {
                            return (
                                <div className='flex flex-col w-full items-center'>
                                    <Link
                                        to={getPolicyDetailsRoute(row?.row?.original)}
                                        className="block mb-1"
                                    >
                                        <span className="hover:underline text-link-main dark:text-darkBg-textLink dark:hover:text-darkBg-textLink">{row.cell.value ?? '-'}</span>
                                    </Link>
                                    {row?.row?.original.priority && (row?.row?.original.priority?.toLowerCase() === 'urgent' || row?.row?.original.priority?.toLowerCase() === 'high') && (
                                        <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${row.row.original.priority.toLowerCase() === 'high'
                                            ? 'bg-yellow-200 text-yellow-800' : 'bg-red-200 text-red-800'}`}>
                                            {priorityName(row.row.original.priority.toLowerCase())}
                                        </div>
                                    )}
                                </div>
                            );
                        }
                    }] : []),
                    {
                        Header: 'Insurer Policy #',
                        accessor: 'insurerPolicyNumber',
                        Cell: (row: any) => {
                            if (!row.cell.value) return '-'
                            return (
                                < RouteLink to={getPolicyDetailsRoute(row?.row?.original)} >
                                    {row.cell.value ?? 'Awaiting Policy Number'}
                                </RouteLink >
                            )

                        }
                    },
                    {
                        Header: 'Policy Type',
                        accessor: 'policyType',
                        Cell: (row: any) => {
                            return <PolicyTypesBadge policyType={row.cell.value} />;
                        }
                    },
                    {
                        Header: 'Premium',
                        accessor: 'premium',
                        Cell: (row: any) => {
                            return <span>{formatCurrencyAmount(row.cell.value) || '-'}</span>;
                        }
                    },
                    {
                        Header: 'Customer name',
                        accessor: 'customerName',
                        Cell: (row: any) => {
                            return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                        }
                    },
                    {
                        Header: 'Customer phone',
                        accessor: 'customerPhone',
                        Cell: (row: any) => {
                            return <span>{getPhoneNumberFromShortCodeFormat(row.cell.value)}</span>;
                        }
                    },
                    {
                        Header: 'Insurer company',
                        accessor: 'insurerName',
                        Cell: (row: any) => {
                            return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                        }
                    },
                    {
                        Header: 'Days to expiry',
                        accessor: 'daysToExpire',
                        Cell: (row: any) => {
                            return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                        }
                    },
                    {
                        Header: 'Expiry date',
                        accessor: 'expiryDate',
                        Cell: (row: any) => {
                            return <span>{row.cell.value ? moment(row.cell.value).format("DD/MM/YYYY") : '-'}</span>;
                        }
                    },
                    {
                        Header: 'Action',
                        id: 'Action',
                        Cell: (row: any) => {
                            return (
                                <GTableActionColumn actions={getTableRowActions(row.row.original)} />
                            );
                        }
                    }
                ]
            }
        ],
        []
    );

    const { data, isLoading } = useQuery({
        queryKey: ['getPolicyRenewals', currentPage, showPerPage, insurerId, expiredTo, expiredFrom, renewalState, month, year, searchString],
        queryFn: getPolicyRenewals,
        staleTime: 1000 * 10,
        placeholderData: (prev) => prev,
        gcTime: 1000 * 60 * 30 * 5,
        select: (response) => {

            return {
                policyRenewals: response?.expiringPolicies?.content,
                renewalMetrics: response?.renewalMetrics,
                monthlyMetrics: response?.monthlyMetrics,
                pagination: {
                    per_page: response?.expiringPolicies?.size,
                    total: response?.expiringPolicies?.totalElements,
                    totalPages: response?.expiringPolicies?.totalPages,
                    from: response?.pageable?.offset + 1,
                    to: response?.totalElements > response?.pageable?.offset + showPerPage
                        ? response?.pageable?.offset + showPerPage : response?.totalElements,
                },
            };
        },
    });

    const pagination = data?.pagination;
    const policyRenewals: IPolicyRenewal[] = data?.policyRenewals;
    const renewalMetrics: IRenewalMetricsData = data?.renewalMetrics;
    const monthlyMetrics: IMonthlyMetricsData = data?.monthlyMetrics;
    console.log("monthlyMetrics", monthlyMetrics)
    const handlePageChange = (page: number) => {
        setSearchParams({ page: String(page) });
    };

    const getPeriodLabelFromDateRange = () => {
        const fromDate = moment(expiredFrom, 'YYYY-MM-DD');
        const toDate = moment(expiredTo, 'YYYY-MM-DD');
        
        if (!fromDate.isValid() || !toDate.isValid()) {
            return 'Custom Range';
        }
        
        // If same month, show month and year
        if (fromDate.isSame(toDate, 'month') && fromDate.isSame(toDate, 'year')) {
            return fromDate.format('MMM YYYY');
        }
        
        // If same year, show month range and year
        if (fromDate.isSame(toDate, 'year')) {
            return `${fromDate.format('MMM')} - ${toDate.format('MMM YYYY')}`;
        }
        
        // Different years, show full date range
        return `${fromDate.format('DD MMM YYYY')} - ${toDate.format('DD MMM YYYY')}`;
    };

    const getBrokerStats = () => {
        const periodLabel = getPeriodLabelFromDateRange();
        
        return [
            // {
            //     title: 'Upcoming',
            //     value: renewalMetrics?.upcomingRenewalsCount,
            //     value2: renewalMetrics?.upcomingRenewalsPremium,
            //     period: 'Up to 3 months',
            //     change: '',
            //     changeType: '',
            //     icon: Clock,
            //     className: 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-white dark:border-darkBg-border',
            //     color: 'blue' as const,
            //     loading: isLoading,
            //     renewalStatus: 'PENDING'
            // },
            {
                title: `Renewals `,
                value: renewalMetrics?.dueForRenewalCount,
                value2: renewalMetrics?.dueForRenewalPremium,
                period: periodLabel,
                change: '',
                changeType: '',
                icon: AlertTriangle,
                className: 'bg-[#FFFBE6] dark:bg-yellow-900/30 text-[#CCAC00] dark:text-yellow-300 border-white dark:border-darkBg-border',
                color: 'gold' as const,
                loading: isLoading,
                renewalStatus: 'DUE'
            },
            // {
            //     title: 'Policies Lost',
            //     value: renewalMetrics.lostBusinessesCount,
            //     value2: renewalMetrics.lostBusinessesPremium,
            //     period: 'Year to Date',
            //     changeType: '',
            //     icon: XCircle,
            //     className: 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-white dark:border-darkBg-border',
            //     color: 'red' as const,
            //     loading: isLoading,
            //     renewalStatus: 'LOST'
            // },
            {
                title: 'Policies Renewed',
                value: renewalMetrics?.renewedPoliciesCount,
                value2: renewalMetrics?.renewedPoliciesPremium,
                period: periodLabel,
                icon: Users,
                className: 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-white dark:border-darkBg-border',
                color: 'blue' as const,
                loading: isLoading,
                renewalStatus: 'RENEWED'
            },
        ];
    };

    const getMetricsCards = () => {
        if (isLoading) {
            return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    {[1, 2].map((item) => (
                        <SkeletonLoader key={item} />
                    ))}
                </div>
            );
        }

        return (
            <div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    {getBrokerStats()?.map((stat, index) => (
                        <div key={index} onClick={() => setRenewalState(stat.renewalStatus)} className={`cursor-pointer p-3 rounded-2xl border !bg-white border-gray-200 dark:!bg-darkBg dark:border-darkBg-border shadow-md ${stat.className}`}>
                            <div className="flex items-center mb-6">
                                <div className={`rounded-full p-3 mr-4 ${stat.className}`}>
                                    <stat.icon className="h-8 w-8" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500 dark:text-darkBg-subText">{stat.title}</p>
                                    <div className="text-md font-bold">
                                        {formatNumberValue(stat.value2).toString().includes('k') ||
                                            formatNumberValue(stat.value2).toString().includes('M') ? (
                                            <ToolTip
                                                placement="bottom"
                                                bgColor="bg-gray-600"
                                                textColor="text-white"
                                                message={formatCurrencyAmount(stat.value2, undefined, true)}
                                            >
                                                <div className="md:text-lg lg:text-md font-bold">
                                                    {formatNumberValue(stat.value2)}
                                                </div>
                                            </ToolTip>
                                        ) : (
                                            <div className="md:text-lg lg:text-md font-bold">
                                                {formatNumberValue(stat.value2)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="border-t border-gray-200 dark:border-darkBg-border space-y-1">
                                <div className="flex justify-between text-xs">
                                    <span className="text-gray-600 dark:text-darkBg-subText">Period:</span>
                                    <span className="font-semibold text-gray-900 dark:text-darkBg-mainText">{stat.period}</span>
                                </div>
                                <div className="flex justify-between text-xs pt-1 border-t border-gray-200 dark:border-darkBg-border">
                                    <span className="text-gray-600 dark:text-darkBg-subText">Policies:</span>
                                    <span className="font-bold text-blue-600 dark:text-blue-400">{`${stat.value}`} {stat.value < 2 ? 'policy' : 'policies'}</span>
                                </div>
                            </div>
                        </div>
                    ))}

                    {/* Monthly Financial Metrics - Summary Bar */}

                </div>

                {/* <div className="mb-6">
                    <div className="flex items-center w-full gap-2 text-sm text-gray-500 mb-3">

                        <div className="flex items-center justify-center gap-2">
                            <CalendarDays className="w-4 h-4 " />
                            <DatePickerInput
                                id="month"
                                selected={selectedYearMonth}
                                onChange={(date: Date | null) => date && setSelectedYearMonth(date)}
                                showMonthYearPicker
                                dateFormat="MMM yyyy"
                                maxDate={new Date()}
                                classes="w-full !w-[120px]"
                            />
                        </div>
                        <div>
                            <span>{monthlyMetrics?.period || '-'}{" "}</span>
                            <span className="text-gray-300">|</span>
                            <span className="text-xs">{" "}{monthlyMetrics?.brokerName || '-'} • {monthlyMetrics?.insurerName || '-'}</span>
                        </div>

                    </div>
                    <div className="flex flex-wrap items-center gap-4 p-4 bg-white rounded-xl border border-gray-200 dark:!bg-darkBg dark:border-darkBg-border">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-green-100 rounded-lg">
                                <CheckCircle2 className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <p className="text-xs text-gray-500">Renewal Rate</p>
                                <p className="text-lg font-bold text-gray-900 dark:text-darkBg-mainText">{formatPercentageValue(monthlyMetrics?.renewalRate)}%</p>
                            </div>
                        </div>
                        <div className="w-px h-10 bg-gray-200 hidden sm:block" />
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-red-100 rounded-lg">
                                <XCircle className="w-5 h-5 text-red-600" />
                            </div>
                            <div>
                                <p className="text-xs text-gray-500">Premium Lost</p>
                                <p className="text-lg font-bold text-red-600">
                                    {formatCurrencyAmount(monthlyMetrics?.renewalAmountLost, undefined, true)}
                                </p>
                            </div>
                        </div>
                        <div className="w-px h-10 bg-gray-200 hidden sm:block" />
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-green-100 rounded-lg">
                                <TrendingUp className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <p className="text-xs text-gray-500">Premium Renewed</p>
                                <p className="text-lg font-bold text-green-600">
                                    {formatCurrencyAmount(monthlyMetrics?.renewalAmountCollected, undefined, true)}
                                </p>
                            </div>
                        </div>
                        <div className="w-px h-10 bg-gray-200 hidden sm:block" />
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <CalendarDays className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-xs text-gray-500">Premium Due</p>
                                <p className="text-lg font-bold text-[#FFD700]">
                                    {formatCurrencyAmount(monthlyMetrics?.renewalAmountDue, undefined, true)}
                                </p>
                            </div>
                        </div>
                        <div className="w-px h-10 bg-gray-200 hidden sm:block" />
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-purple-100 rounded-lg">
                                <TrendingDown className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                                <p className="text-xs text-gray-500">Policies Lost / Renewed</p>
                                <p className="text-lg font-bold text-gray-900">
                                    <span className="text-red-600">{monthlyMetrics?.policiesLost}</span>
                                    <span className="text-gray-400 mx-1">/</span>
                                    <span className="text-green-600">{monthlyMetrics?.policiesRenewed}</span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div> */}
            </div>

        );
    };

    const getFilter = () => {
        return (
            <div className="flex flex-col items-center gap-2 max-w-[10rem]">
                <TableFilter
                    openFilter={openFilter}
                    onFilterChange={(filterState: boolean) => setOpenFilter(filterState)}
                    selectedFilters={tableFilters}
                    content={
                        <TableFilterForm
                            filters={tableFilters}
                            getValues={(filters: ITableFilterItem[]) => {
                                setFilters(filters);
                                setOpenFilter(false);
                            }}
                            onResetFilters={() => setFilters(getDefaultFilters())}
                        />
                    }
                />
            </div>
        )
    }

    return (
        <Page title="Renewals">
            <div ref={pageRef} className="bg-white dark:bg-darkBg w-full p-5 min-h-full rounded-lg shadow-md">
                <div className="flex flex-col md:flex-row justify-between md:items-center">
                    <div>
                        <div className="inline-flex items-center gap-2 mb-2">
                            <div className="h-8 w-2 bg-gradient-to-b from-yellow-500 to-yellow-700 rounded"></div>
                            <h2 className="font-semibold text-3xl text-gray-900 dark:text-darkBg-mainText">Renewals</h2>
                        </div>

                        <p className="text-sm text-slate-400 dark:text-darkBg-subText">
                            View a list of all renewals and associated details.
                        </p>
                    </div>
                    {getFilter()}
                </div>

                <div className='py-4 mt-4'>
                    {getMetricsCards()}
                </div>

                <div className=''>
                    <GTable
                        columns={columns}
                        data={policyRenewals || []}
                        classes="h-fit"
                        loading={isLoading}
                        onSearch={(param) => setSearchString(param)}
                        searchPlaceholder='Search by customer name'
                        paginationData={pagination}
                        onPageChange={handlePageChange}
                        showPerPage={showPerPage}
                        onShowPerPageChange={(page: number) => setShowPerPage(page)}
                        exportFilteredData={exportRenewalsFromFilters}
                        tableType={getSelectedMetric()}
                        stickyHeader
                        selectedFilters={tableFilters}
                        onRemoveFilter={(filter: ITableFilterItem, index: number) => onRemoveFilter(filter, index, tableFilters, setFilters)}

                    />
                </div>
            </div>
            <DownloadHookModal />
            <PolicyModalComponent />
            <ModalComponent />
        </Page>
    )
}

export default Renewals


// SkeletonLoader component for metrics cards
const SkeletonLoader = () => (
    <div className="p-3 rounded-lg border border-gray-200 bg-white dark:bg-darkBg shadow-md">
        <div className="flex items-center mb-6">
            <div className="rounded-full bg-gray-200 dark:bg-darkBg-border p-3 mr-4 animate-pulse h-14 w-14"></div>
            <div className="w-full">
                <div className="h-4 bg-gray-200 dark:bg-darkBg-border rounded w-1/2 mb-2 animate-pulse"></div>
                <div className="h-6 bg-gray-200 dark:bg-darkBg-border rounded w-3/4 animate-pulse"></div>
            </div>
        </div>
        <div className="border-t border-gray-200 dark:border-darkBg-border space-y-3 pt-3">
            <div className="flex justify-between">
                <div className="h-3 bg-gray-200 dark:bg-darkBg-border rounded w-1/4 animate-pulse"></div>
                <div className="h-3 bg-gray-200 dark:bg-darkBg-border rounded w-1/3 animate-pulse"></div>
            </div>
            <div className="flex justify-between pt-2">
                <div className="h-3 bg-gray-200 dark:bg-darkBg-border rounded w-1/4 animate-pulse"></div>
                <div className="h-3 bg-gray-200 dark:bg-darkBg-border rounded w-1/4 animate-pulse"></div>
            </div>
        </div>
    </div>
);

export interface IPolicyRenewal {
    policyNumber: string;
    policyType: string;
    premium: number;
    priority: string;
    customerName: string;
    customerPhone: string;
    daysToExpire: number;
    insurerName: string;
    renewed: boolean
}

interface IRenewalMetricsData {
    upcomingRenewalsPremium: number;
    lostBusinessesPremium: number;
    lostBusinessesCount: number;
    dueForRenewalPremium: number;
    renewalRate: number;
    dueForRenewalCount: number;
    renewedPoliciesCount: number;
    renewedPoliciesPremium: number;
    upcomingRenewalsCount: number;
}

interface IMonthlyMetricsData {
    insurerName: string;
    brokerName: string;
    month: number;
    monthName: string;
    period: string;
    year: string;
    dueForRenewalCount: number;
    policiesDueForRenewal: number;
    policiesLost: number;
    policiesPending: number;
    policiesRenewed: number;
    renewalAmountCollected: number;
    renewalAmountDue: number;
    renewalAmountLost: number;
    renewalRate: number;
}
